/**
 * Tags Google par boutique — gtag.js (Google Ads `AW-…`, Google Analytics 4
 * `G-…`) et/ou conteneur Google Tag Manager (`GTM-…`).
 *
 * Les identifiants viennent de /site-config (lib/siteConfig.js), donc de la
 * boutique résolue par le domaine : chaque boutique charge SES tags, et une
 * boutique sans identifiant ne charge rien du tout.
 *
 * Décision produit (cadrage du 15/09/2026) : les tags se chargent sur chaque
 * page, indépendamment du bandeau cookies (lib/consent.js), qui ne pilote que
 * la mesure interne (Régie). Attention : c'est un choix assumé par
 * l'opérateur, pas une recommandation CNIL.
 *
 * Le site est une SPA : gtag est configuré avec `send_page_view: false` et
 * c'est `trackGooglePageView` (appelé par Layout à chaque changement de
 * route) qui envoie les vues de page — sinon seule la première page
 * chargée serait comptée. Les événements émis AVANT que /site-config ait
 * répondu sont mis en attente puis rejoués, dans l'ordre, une fois les tags
 * installés.
 */
import { loadSiteConfig } from "@/lib/siteConfig";

export const CURRENCY = "EUR";

let installed = false;      // les balises <script> ont été injectées
let ready = false;          // config résolue (installée ou "rien à charger")
let ids = { ads: "", ga4: "", gtm: "", purchaseLabel: "", leadLabel: "" };
let pendingPageView = null; // dernière route vue avant l'installation ({pathname, search, href})
// Vue de page en attente de SON titre (voir trackGooglePageView), et les
// événements émis entre-temps, envoyés juste après elle pour garder l'ordre.
let titleWait = null;       // { pathname, path, href, timer }
let afterTitleWait = [];
const titleByPath = new Map(); // titre connu de chaque page (pathname → titre)
let flushBound = false;
export const TITLE_WAIT_MS = 1500;
let lastPageView = null;    // URL de la dernière vue envoyée (anti-doublon)
let lastViewItem = null;    // produit du dernier view_item depuis cette vue
let lastLeadAt = 0;         // horodatage du dernier generate_lead
const sentPurchases = new Set(); // commandes déjà envoyées dans cet onglet
let pendingEvents = [];     // appels (fonctions) émis avant l'installation
let installPromise = null;

function hasGtag() {
  return !!(ids.ads || ids.ga4);
}

function hasAnything() {
  return hasGtag() || !!ids.gtm;
}

function dataLayerPush(...args) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(...args);
}

// Le vrai `gtag` de Google est exactement cette fonction : un push de
// l'objet `arguments` (et non d'un tableau) dans dataLayer. gtag.js relit
// la file au chargement, donc les appels faits avant que le script soit
// arrivé ne sont pas perdus.
function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
}

function addScript(src, testId) {
  const el = document.createElement("script");
  el.async = true;
  el.src = src;
  el.setAttribute("data-google-tag", testId);
  document.head.appendChild(el);
  return el;
}

function injectGtag() {
  const primary = ids.ads || ids.ga4;
  addScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primary)}`, "gtag");
  window.gtag = window.gtag || gtag;
  window.gtag("js", new Date());
  // send_page_view: false — les vues de page sont envoyées par
  // trackGooglePageView à chaque navigation (SPA), jamais par gtag lui-même.
  if (ids.ads) window.gtag("config", ids.ads, { send_page_view: false });
  if (ids.ga4) window.gtag("config", ids.ga4, { send_page_view: false });
}

function injectGtm() {
  // Équivalent exact du snippet officiel « Google Tag Manager » (head).
  dataLayerPush({ "gtm.start": Date.now(), event: "gtm.js" });
  addScript(`https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(ids.gtm)}`, "gtm");
  // Snippet <noscript> (body) : sans intérêt dans une SPA — un navigateur
  // sans JavaScript n'affiche de toute façon aucune page de ce site.
}

export function readGoogleIds(config) {
  return {
    ads: (config?.google_ads_id || "").trim(),
    ga4: (config?.ga4_id || "").trim(),
    gtm: (config?.gtm_id || "").trim(),
    purchaseLabel: (config?.google_ads_purchase_label || "").trim(),
    leadLabel: (config?.google_ads_lead_label || "").trim(),
  };
}

/** Installe les tags une seule fois à partir de la config publique. */
export function installGoogleTags(config) {
  if (installed || typeof document === "undefined") return;
  ids = readGoogleIds(config);
  ready = true;
  if (!hasAnything()) {
    pendingPageView = null;
    pendingEvents = [];
    return;
  }
  installed = true;
  if (hasGtag()) injectGtag();
  if (ids.gtm) injectGtm();

  // Rejoue ce qui a été émis pendant l'attente de /site-config : d'abord la
  // page courante (une seule, la dernière — les précédentes n'ont pas été
  // vues assez longtemps pour compter), puis les événements dans l'ordre.
  const view = pendingPageView;
  const queued = pendingEvents;
  pendingPageView = null;
  pendingEvents = [];
  if (view) trackGooglePageView(view.pathname, view.search, view.href);
  queued.forEach((replay) => replay());
}

/** Charge /site-config (mis en cache) puis installe. Idempotent. */
export function ensureGoogleTags() {
  if (!installPromise) {
    installPromise = loadSiteConfig().then((config) => installGoogleTags(config));
  }
  return installPromise;
}

/**
 * Titre d'une page, annoncé par la couche SEO (lib/seo.js) dès qu'il est
 * connu — c'est la source de vérité du `page_title`, pas `document.title`.
 *
 * Pourquoi : Layout signale la vue de page au changement de route, AVANT que
 * la nouvelle page n'ait reçu son titre (il arrive de l'API SEO). Lire
 * `document.title` à ce moment-là donnait le titre de la page PRÉCÉDENTE —
 * dans Google Analytics, toutes les pages se retrouvaient sous le même nom.
 */
export function announcePageTitle(title, pathname) {
  const t = String(title || "").trim();
  const path = pathname || (typeof window !== "undefined" ? window.location.pathname : "");
  if (!t || !path) return;
  titleByPath.set(path, t);
  if (titleWait && titleWait.pathname === path) flushTitleWait(t);
}

function sendPageView({ path, href }, title) {
  const params = { page_path: path, page_location: href, page_title: title };
  if (hasGtag()) window.gtag("event", "page_view", params);
  // Côté GTM, une SPA doit signaler ses navigations par un événement
  // dataLayer que le conteneur écoute (déclencheur « Événement personnalisé »
  // sur `page_view`) — GTM ne voit pas les changements d'URL tout seul.
  if (ids.gtm) dataLayerPush({ event: "page_view", ...params });
}

function flushTitleWait(title) {
  if (!titleWait) return;
  const view = titleWait;
  titleWait = null;
  clearTimeout(view.timer);
  // Sans titre annoncé à temps (page sans référencement, API lente) : le
  // titre déjà connu pour ce chemin, sinon celui du document.
  const finalTitle = title || titleByPath.get(view.pathname) || (typeof document !== "undefined" ? document.title : "");
  sendPageView(view, finalTitle);
  const queued = afterTitleWait;
  afterTitleWait = [];
  queued.forEach((fn) => fn());
}

function bindFlushOnLeave() {
  if (flushBound || typeof window === "undefined") return;
  flushBound = true;
  // L'onglet se ferme ou passe en arrière-plan avant l'arrivée du titre : la
  // vue et les événements en attente partent quand même.
  window.addEventListener("pagehide", () => flushTitleWait());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushTitleWait();
  });
}

/** Exécute `fn` tout de suite, ou juste après la vue de page en attente de
 * son titre : un view_item ne doit jamais précéder le page_view de sa page. */
function afterPageView(fn) {
  if (titleWait) afterTitleWait.push(fn);
  else fn();
}

/**
 * Vue de page — appelée par Layout à CHAQUE changement de route (site en
 * page unique : sans cet appel, seule la première page serait comptée).
 *
 * L'envoi attend le titre de la nouvelle page (announcePageTitle), au plus
 * TITLE_WAIT_MS, pour que `page_title` soit le bon. Un titre déjà connu pour
 * ce chemin (page revisitée) part immédiatement.
 *
 * Deux appels consécutifs pour la même URL ne font qu'une vue : en
 * développement React rejoue les effets au montage, et recliquer le lien de
 * la page où l'on se trouve n'est pas une nouvelle page vue.
 */
export function trackGooglePageView(pathname, search = "", href) {
  const path = `${pathname}${search || ""}`;
  const location = href || (typeof window !== "undefined" ? window.location.href : path);
  if (!ready) {
    pendingPageView = { pathname, search, href: location };
    return;
  }
  if (!installed || path === lastPageView) return;
  lastPageView = path;
  lastViewItem = null;
  bindFlushOnLeave();
  // La page précédente n'a pas reçu son titre avant qu'on la quitte.
  flushTitleWait();

  const view = { pathname, path, href: location };
  const known = titleByPath.get(pathname);
  if (known) {
    sendPageView(view, known);
    return;
  }
  titleWait = { ...view, timer: setTimeout(() => flushTitleWait(), TITLE_WAIT_MS) };
}

/**
 * Événement GA4 standard (view_item, add_to_cart, begin_checkout, purchase,
 * generate_lead…). `params` suit le schéma e-commerce GA4 : { currency,
 * value, transaction_id, items: [{ item_id, item_name, price, quantity }] }.
 */
export function trackGoogleEvent(name, params = {}) {
  if (!ready) {
    pendingEvents.push(() => trackGoogleEvent(name, params));
    return;
  }
  if (!installed) return;
  if (titleWait) {
    afterPageView(() => trackGoogleEvent(name, params));
    return;
  }
  const clean = withoutUndefined(params);
  if (hasGtag()) window.gtag("event", name, clean);
  if (ids.gtm) {
    // Convention GTM / GA4 : TOUTES les données e-commerce sous la clé
    // `ecommerce` (items, value, currency, transaction_id, shipping…), les
    // autres paramètres à la racine. La clé est remise à null juste avant,
    // pour qu'un événement n'hérite jamais des produits du précédent
    // (recommandation officielle de Google).
    const ecommerce = {};
    const rest = {};
    Object.entries(clean).forEach(([k, v]) => {
      (ECOMMERCE_KEYS.has(k) ? ecommerce : rest)[k] = v;
    });
    const hasEcommerce = Object.keys(ecommerce).length > 0;
    if (hasEcommerce) dataLayerPush({ ecommerce: null });
    dataLayerPush({ event: name, ...rest, ...(hasEcommerce ? { ecommerce } : {}) });
  }
}

const ECOMMERCE_KEYS = new Set(["items", "value", "currency", "transaction_id", "shipping", "tax", "coupon"]);

function withoutUndefined(obj) {
  return Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v !== undefined));
}

function toNumber(n) {
  const v = typeof n === "string" ? Number(n) : n;
  return typeof v === "number" && Number.isFinite(v) ? Math.round(v * 100) / 100 : undefined;
}

/**
 * Conversion Google Ads (gtag « conversion » avec `send_to: AW-xxx/label`).
 * N'envoie rien si la boutique n'a pas renseigné de libellé pour ce type de
 * conversion — l'événement GA4 correspondant est de toute façon envoyé à
 * part par l'appelant.
 */
function trackAdsConversion(labelKey, params) {
  if (!ready) {
    pendingEvents.push(() => trackAdsConversion(labelKey, params));
    return;
  }
  const label = ids[labelKey];
  if (!installed || !ids.ads || !label) return;
  if (titleWait) {
    afterPageView(() => trackAdsConversion(labelKey, params));
    return;
  }
  window.gtag("event", "conversion", withoutUndefined({ send_to: `${ids.ads}/${label}`, ...params }));
}

export function itemFromProduct(product, quantity = 1) {
  return withoutUndefined({
    item_id: String(product.product_id || product.id || product.slug || ""),
    item_name: product.name,
    item_category: product.category || undefined,
    price: toNumber(product.price),
    quantity: Math.max(1, Math.round(Number(quantity) || 1)),
  });
}

function itemsValue(items) {
  return Math.round(items.reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0) * 100) / 100;
}

/** Fiche produit ouverte. Une seule fois par produit et par vue de page
 * (React rejoue le chargement de la fiche en développement). */
export function trackViewItem(product) {
  if (!product) return;
  const item = itemFromProduct(product);
  if (item.item_id && item.item_id === lastViewItem) return;
  lastViewItem = item.item_id;
  const items = [item];
  trackGoogleEvent("view_item", { currency: CURRENCY, value: itemsValue(items), items });
}

export function trackAddToCart(product, quantity = 1) {
  const items = [itemFromProduct(product, quantity)];
  trackGoogleEvent("add_to_cart", { currency: CURRENCY, value: itemsValue(items), items });
}

/** Ouverture du formulaire de commande : `value` = total du panier, hors
 * livraison (calculée à l'étape suivante). */
export function trackBeginCheckout(cartItems) {
  if (!cartItems?.length) return;
  const items = cartItems.map((i) => itemFromProduct(i, i.quantity));
  trackGoogleEvent("begin_checkout", { currency: CURRENCY, value: itemsValue(items), items });
}

const PURCHASE_KEY = "lumiere_ga_purchases_sent";

/**
 * Achat confirmé. `transaction_id` = identifiant unique de la commande.
 *
 * Envoyé UNE fois par commande, même si la page de succès est rechargée,
 * rouverte depuis l'historique ou dans un autre onglet : les commandes
 * envoyées sont mémorisées dans le navigateur (localStorage, et en mémoire
 * si le stockage est bloqué). GA4 déduplique aussi sur transaction_id, mais
 * Google Ads et la plupart des balises GTM tierces ne le font pas.
 */
export function trackPurchase(order) {
  const id = order?.id ? String(order.id) : "";
  if (!id || sentPurchases.has(id)) return;
  sentPurchases.add(id);
  try {
    const sent = JSON.parse(localStorage.getItem(PURCHASE_KEY) || "[]");
    if (Array.isArray(sent) && sent.includes(id)) return;
    localStorage.setItem(PURCHASE_KEY, JSON.stringify([...(Array.isArray(sent) ? sent : []), id].slice(-50)));
  } catch {
    /* stockage indisponible : la mémoire de l'onglet protège déjà du rechargement */
  }
  const items = (order.items || []).map((i) => itemFromProduct(i, i.quantity));
  const params = {
    transaction_id: id,
    currency: String(order.currency || CURRENCY).toUpperCase(),
    value: toNumber(order.total) ?? itemsValue(items),
    shipping: toNumber(order.shipping_cost),
    items,
  };
  trackGoogleEvent("purchase", params);
  trackAdsConversion("purchaseLabel", { value: params.value, currency: params.currency, transaction_id: id });
}

/**
 * Demande de contact / devis / rappel envoyée avec succès. À n'appeler
 * qu'APRÈS la réponse OK du serveur. Un second appel dans les 5 secondes est
 * ignoré : un double envoi (double clic, formulaire rendu deux fois) ne doit
 * pas compter deux prospects.
 */
const LEAD_DEDUP_MS = 5000;
export function trackLead(source = "contact") {
  const now = Date.now();
  if (now - lastLeadAt < LEAD_DEDUP_MS) return;
  lastLeadAt = now;
  trackGoogleEvent("generate_lead", { lead_source: source });
  trackAdsConversion("leadLabel", {});
}

/** Réservé aux tests : remet le module à l'état initial. */
export function _resetGoogleTagsForTests() {
  installed = false;
  ready = false;
  ids = { ads: "", ga4: "", gtm: "", purchaseLabel: "", leadLabel: "" };
  pendingPageView = null;
  pendingEvents = [];
  installPromise = null;
  lastPageView = null;
  lastViewItem = null;
  if (titleWait) clearTimeout(titleWait.timer);
  titleWait = null;
  afterTitleWait = [];
  titleByPath.clear();
  lastLeadAt = 0;
  sentPurchases.clear();
  if (typeof window !== "undefined") {
    delete window.dataLayer;
    delete window.gtag;
    document.querySelectorAll("script[data-google-tag]").forEach((el) => el.remove());
  }
}
